import type { RouteLocationNormalized } from 'vue-router'

// Anonym besøgsstatistik (pendant til telemetry.ts, som kun rapporterer fejl). Hver
// navigation lægges i en lille kø og sendes samlet til /api/public/pageviews; backenden
// koger IP + user-agent ned til en dagsroterende hash og gemmer hverken dem eller noget
// bruger-id. Intet gemmes i browseren — ingen cookie, intet localStorage-id, og derfor
// heller ikke noget samtykkebanner.
//
// Alt er fire-and-forget som i telemetry.ts: statistik må aldrig kunne vælte appen.

const ENDPOINT = '/api/public/pageviews'
/** Backendens loft pr. beacon — mere end 10 events i én forsendelse bliver alligevel smidt væk. */
const MAX_BATCH = 10
const FLUSH_DELAY_MS = 2000

interface PageViewEvent {
  routeName: string
  divesiteId?: string
  locale: string
  standalone: boolean
  referrer?: string
}

const queue: PageViewEvent[] = []
let timer: ReturnType<typeof setTimeout> | undefined
let installed = false
let referrerSent = false
let lastFullPath: string | undefined

/** Kun en ekstern henvisende vært er interessant — og kun én gang pr. sidevisning i browseren. */
function externalReferrerHost(): string | undefined {
  try {
    if (referrerSent || !document.referrer) return undefined
    const host = new URL(document.referrer).host
    return host && host !== location.host ? host : undefined
  } catch {
    return undefined
  }
}

function isStandalone(): boolean {
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      // iOS Safari har sin egen ikke-standard markør for "føjet til hjemmeskærm".
      (navigator as { standalone?: boolean }).standalone === true
    )
  } catch {
    return false
  }
}

export function trackPageview(to: RouteLocationNormalized): void {
  // Hele funktionen er pakket ind: statistik må aldrig kaste videre op i appen.
  try {
    const routeName = typeof to.name === 'string' ? to.name : ''
    if (!routeName) return
    // Admins egne klik forurener tallene. Serveren filtrerer også, men så sparer vi kaldet.
    if (routeName.startsWith('admin')) return
    // En navigation til præcis samme URL (fx et gen-klik i menuen) er ikke en ny visning.
    if (to.fullPath === lastFullPath) return
    lastFullPath = to.fullPath

    const referrer = externalReferrerHost()
    if (referrer) referrerSent = true

    queue.push({
      // Rutenavnet, aldrig stien: "site" i stedet for /sites/<id>, så opgørelsen ikke
      // drukner i ét kort pr. sted — stedet rejser separat som divesiteId.
      routeName,
      divesiteId: routeName === 'site' && typeof to.params.id === 'string' ? to.params.id : undefined,
      // i18n sætter <html lang> ved opstart og ved sprogskift, så den er sandheden her.
      locale: document.documentElement.lang || 'da',
      standalone: isStandalone(),
      referrer,
    })

    ensureFlushListeners()
    if (queue.length >= MAX_BATCH) flushPageviews()
    else schedule()
  } catch {
    // Med vilje tom.
  }
}

function schedule(): void {
  if (timer !== undefined) return
  timer = setTimeout(flushPageviews, FLUSH_DELAY_MS)
}

export function flushPageviews(): void {
  if (timer !== undefined) {
    clearTimeout(timer)
    timer = undefined
  }
  if (queue.length === 0) return

  const body = JSON.stringify(queue.splice(0, MAX_BATCH))

  try {
    const blob = new Blob([body], { type: 'application/json' })
    // sendBeacon overlever at siden lukkes; det gør en almindelig fetch ikke.
    if (!navigator.sendBeacon?.(ENDPOINT, blob)) {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // Kunne ikke sendes. En tabt visning er bedre end at forstyrre brugeren.
  }

  if (queue.length > 0) schedule()
}

/** Sidste chance for at få køen afsted, inden siden lukkes eller skjules. */
function ensureFlushListeners(): void {
  if (installed) return
  installed = true
  window.addEventListener('pagehide', flushPageviews)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPageviews()
  })
}
